import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import './Messages.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Messages() {
  const { user, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef();
  const hasStartedConv = useRef(false);

  // Auto-start conversation from product enquiry
  const startBusinessId = searchParams.get('businessId');
  const startProduct = searchParams.get('product');

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchConversations();
  }, [isLoggedIn]);

  useEffect(() => {
    // Auto start conversation if came from product enquiry
    if (startBusinessId && isLoggedIn && !hasStartedConv.current) {
      hasStartedConv.current = true;
      api.post('/api/chat/start', {
        businessId: startBusinessId,
        productName: startProduct || '',
        firstMessage: startProduct
          ? `Hi, I am interested in your product: *${startProduct}*`
          : 'Hi, I would like to enquire about your business.',
      }).then(res => {
        fetchConversations();
        setActiveConv(res.data.data);
      }).catch(() => {});
    }
  }, [startBusinessId, isLoggedIn]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConv?.messages]);

  const fetchConversations = () => {
    api.get('/api/chat')
      .then(res => setConversations(res.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const openConv = async (conv) => {
    const res = await api.get(`/api/chat/${conv._id}`).catch(() => null);
    if (res) setActiveConv(res.data.data);
  };

  const sendMessage = async () => {
    if (!text.trim() || !activeConv || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/api/chat/${activeConv._id}/send`, { text: text.trim() });
      setActiveConv(res.data.data);
      setText('');
      fetchConversations();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  if (!isLoggedIn) return (
    <div className="msg-page">
      <div className="msg-login-wall">
        <span style={{ fontSize: 56 }}>💬</span>
        <p style={{ fontSize: 16, fontWeight: 600 }}>Please login to view messages</p>
        <button onClick={() => navigate('/login')}
          style={{ background: '#00C853', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          Login
        </button>
      </div>
    </div>
  );

  const getOtherName = (conv) => {
    if (!conv) return '';
    const isBuyer = conv.buyerId?._id === user?.id || conv.buyerId?._id?.toString() === user?.id;
    return isBuyer ? (conv.sellerId?.name || 'Seller') : (conv.buyerId?.name || 'Buyer');
  };

  const getLogo = (conv) => conv?.businessId?.logo ? `${BASE}/${conv.businessId.logo}` : null;

  return (
    <div className="msg-page">
      {/* Sidebar */}
      <div className={`msg-sidebar ${!activeConv ? 'show' : ''}`}>
        <div className="msg-sidebar-header">
          <h2>💬 Messages</h2>
        </div>
        <div className="msg-conv-list">
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
              <p style={{ fontSize: 13 }}>No conversations yet</p>
            </div>
          ) : (
            conversations.map(conv => (
              <div key={conv._id} className={`msg-conv-item ${activeConv?._id === conv._id ? 'active' : ''}`}
                onClick={() => openConv(conv)}>
                <div className="msg-conv-avatar">
                  {getLogo(conv) ? <img src={getLogo(conv)} alt="" /> : '🏢'}
                </div>
                <div className="msg-conv-info">
                  <p className="msg-conv-name">{conv.businessId?.businessName || 'Business'}</p>
                  {conv.productName && <p className="msg-conv-product">📦 {conv.productName}</p>}
                  <p className="msg-conv-last">{conv.lastMessage}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`msg-chat ${activeConv ? 'show' : ''}`}>
        {!activeConv ? (
          <div className="msg-empty">
            <span>💬</span>
            <p>Select a conversation to start chatting</p>
          </div>
        ) : (
          <>
            <div className="msg-chat-header">
              <button onClick={() => setActiveConv(null)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b', marginRight: 4 }}>←</button>
              <div className="msg-chat-header-avatar">
                {getLogo(activeConv) ? <img src={getLogo(activeConv)} alt="" /> : '🏢'}
              </div>
              <div className="msg-chat-header-info">
                <h3>{activeConv.businessId?.businessName || 'Business'}</h3>
                {activeConv.productName && <p>📦 {activeConv.productName}</p>}
              </div>
            </div>

            <div className="msg-messages">
              {(activeConv.messages || []).map((msg, i) => {
                const isMine = msg.senderId?.toString() === user?.id || msg.senderId === user?.id;
                return (
                  <div key={i} className={`msg-bubble-wrap ${isMine ? 'mine' : 'theirs'}`}>
                    <div className={`msg-bubble ${isMine ? 'mine' : 'theirs'}`}>{msg.text}</div>
                    <span className="msg-time">
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="msg-input-row">
              <input className="msg-input" placeholder="Type a message..."
                value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKey} />
              <button className="msg-send-btn" onClick={sendMessage} disabled={sending || !text.trim()}>➤</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
